import {
  Heading,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  ListItem,
  Spacer,
  UnorderedList,
  ButtonGroup,
  Button,
  IconButton,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Text,
  useToast,
  Tag
} from "@chakra-ui/react";
import {
  MdDelete,
  MdFileOpen,
} from "react-icons/md";
import { FileUploader } from "react-drag-drop-files";
import { useEffect, useState } from "react";
import { deleteIndicatorSource, postIndicatorSource } from "service/api/document.api";
import { useAppDispatch } from "store/store.hook";
import { addSource, removeSource } from "store/reducers/documents.slice";
import { explodeIndicatorId, getUrl } from "utils/utils";
import { Attachment } from "types/Attachment";
import { Source } from "types/Source";

interface AttachmentsListProps {
  documentId: string;
  path: string;
  index: string;
  attachments: Attachment[];
  isReadOnly: boolean;
  checkPermission: boolean;
  sources: string[];
}

/**
 * Component for displaying and managing a list of attachments for a document.
 *
 * @component
 * @param {string} documentId - The ID of the document to which the attachments belong.
 * @param {string} path - The path of the document.
 * @param {string} index - The index of the document.
 * @param {Attachment[]} attachments - The list of attachments.
 * @param {boolean} isReadOnly - Flag indicating if the attachments are read-only.
 * @param {boolean} checkPermission - Flag indicating if the user has permission to modify attachments.
 * @param {string[]} sources - The list of sources.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <AttachmentsList
 *   documentId="MongoDBObjectId"
 *   path="path/to/document"
 *   index="1"
 *   attachments={attachmentsArray}
 *   isReadOnly={false}
 *   checkPermission={true}
 *   sources={sourcesArray}
 * />
 */

export default function AttachmentsList({ documentId, path, index, attachments, isReadOnly, checkPermission, sources }: AttachmentsListProps) {
  const { isOpen: isOpenUpload, onOpen: onOpenUpload, onClose: onCloseUpload } = useDisclosure()
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()
  const dispatch = useAppDispatch();
  const [file, setFile] = useState(null);
  const [fileNameToDelete, setFileNameToDelete] = useState("");
  const [attachmentsState, setAttachmentsState] = useState<Attachment[]>(attachments);
  const toast = useToast()

  const handleFileChange = (file: any) => {
    setFile(file);
  };

  const handleFileUpload = () => {
    if (file) {
      postIndicatorSource(documentId, path, file[0]).then((res) => {

        const source: Attachment = res.data
        const { targetId, subTargetId, actionId, indicatorId } = explodeIndicatorId(path)
        dispatch(addSource({ targetId, subTargetId, actionId, indicatorId, source }))
        onCloseUpload()
        if (attachmentsState == null || attachmentsState?.length == 0) setAttachmentsState([source])
        else
          setAttachmentsState([...attachmentsState, source])

        toast({
          title: "File caricato con successo!",
          status: "success",
          duration: 6000,
          isClosable: true,
        })

      }).catch((err) => {
        toast({
          title: "Errore nel caricamento del file",
          status: "error",
          duration: 6000,
          isClosable: true,
        })
      })
    }
  }

  const handleOnOpenDelete = (filename: string) => {
    onOpenDelete()
    setFileNameToDelete(filename)
  }

  const handleFileDelete = () => {

    deleteIndicatorSource(documentId, path, fileNameToDelete).then((res) => {
      const { targetId, subTargetId, actionId, indicatorId } = explodeIndicatorId(path)
      setAttachmentsState(attachmentsState.filter((attachement) => attachement.filename != fileNameToDelete))
      dispatch(removeSource({ targetId, subTargetId, actionId, indicatorId, filename: fileNameToDelete }))
      onCloseDelete()
      toast({
        title: "File eliminato con successo!",
        status: "success",
        duration: 6000,
        isClosable: true,
      })
    }).catch((err) => {
      toast({
        title: "Errore nell'eliminazione del file",
        status: "error",
        duration: 6000,
        isClosable: true,
      })
    })
  }


  useEffect(() => {
    if (isOpenUpload == false) {
      setFile(null)
    }
  }, [isOpenUpload])


  return (
    <>
      <Modal isOpen={isOpenUpload} onClose={onCloseUpload} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Caricamento di una nuova fonte per l'indice {index}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FileUploader
              multiple={true}
              handleChange={handleFileChange}
              name="file"
              types={["JPEG", "PNG", "GIF", "PDF", "DOC", "DOCX", "XLS", "XLSX"]}
            />
            <p>{file ? `File name: ${file[0].name}` : "no files uploaded yet"}</p>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onCloseUpload}>
              Annulla
            </Button>
            <Button colorScheme='green' onClick={handleFileUpload} isDisabled={file == null}>Carica il file</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenDelete} onClose={onCloseDelete} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confermi l'eliminazione della fonte?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Confermando l'eliminazione la fonte verrà rimossa definitivamente dal sistema.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme='blue' mr={3} onClick={onCloseDelete}>
              Annulla
            </Button>
            <Button colorScheme='red' onClick={handleFileDelete} >Elimina definitivamente</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Accordion allowMultiple backgroundColor={"white"}>
        <AccordionItem>
          <AccordionButton>
            <Box flex='1' textAlign='left'>
              <Heading size="xs" as="h5" textTransform="uppercase" alignItems={"center"}>
                {
                  !checkPermission ?
                    <Tag colorScheme='red' >Allegati non modificabili 🔒</Tag>
                    :
                    <span>Allegati</span>
                }
              </Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={5}>
            {
              attachmentsState && attachmentsState?.length == 0 ?
                <Heading size="xs" as="h6">
                  Nessuna fonte caricata.
                </Heading>
                :
                <UnorderedList>
                  {
                     attachmentsState?.map(
                      (source) => {
                        return (
                          <ListItem>

                            <HStack>
                              <a href={getUrl(documentId, path, source.filename)} target="_blank">
                                {source.originalname}
                              </a>

                              <Spacer />

                              <ButtonGroup variant='outline' spacing='4'>
                                <a href={getUrl(documentId, path, source.filename)} target="_blank">
                                  <IconButton
                                    colorScheme='blue'
                                    aria-label='Open or download file'
                                    icon={<MdFileOpen />}
                                    variant='solid'
                                    size={"xs"}

                                  />
                                </a>
                                {
                                  isReadOnly == false &&
                                  <a>
                                    <IconButton
                                      colorScheme='red'
                                      aria-label='Delete file'
                                      icon={<MdDelete />}
                                      variant='solid'
                                      size={"xs"}
                                      isDisabled={checkPermission == false}

                                      onClick={() => handleOnOpenDelete(source.filename)}
                                    />
                                  </a>
                                }
                              </ButtonGroup>
                            </HStack>
                          </ListItem>
                        )
                      }
                    )
                  }
                </UnorderedList>
            }
            {
              isReadOnly == false &&
              <Button onClick={onOpenUpload} isDisabled={checkPermission == false} colorScheme="green"
              >Carica un nuovo allegato
              </Button>
            }
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>

  );
}
